import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Upload, ArrowRight, ArrowLeft, CheckCircle2, XCircle,
  Download, Loader2, AlertTriangle, RefreshCcw, Sparkles,
  FileSpreadsheet, BarChart3,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { FileUploadZone } from './FileUploadZone';
import { FieldMappingStep } from './FieldMappingStep';
import { generateRows, rowsToCsv, downloadBlob, downloadXlsx } from './sampleData';

interface ParseResult {
  jobId: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  totalRows: number;
  headers: string[];
  sampleRows: Record<string, string>[];
  suggestedMappings: Record<string, string>;
}

interface ImportJob {
  id: string;
  jobName: string;
  fileName: string;
  status: string;
  totalRows: number;
  importedRows: number;
  updatedRows: number;
  skippedRows: number;
  errorRows: number;
  errorSummary: string[] | null;
  completedAt: string | null;
}

type Step = 1 | 2 | 3 | 4;

const STEP_LABELS: Record<Step, string> = {
  1: 'Upload File',
  2: 'Map Fields',
  3: 'Review & Import',
  4: 'Complete',
};

function StepIndicator({ current }: { current: Step }) {
  const steps: Step[] = [1, 2, 3, 4];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 28 }}>
      {steps.map((s) => {
        const done = s < current;
        const active = s === current;
        return (
          <div key={s} style={{ display: 'flex', alignItems: 'center', flex: s < 4 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 52 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? '#22c55e' : active ? '#3b82f6' : 'rgba(255,255,255,0.06)',
                border: `2px solid ${done ? '#22c55e' : active ? '#60a5fa' : 'rgba(255,255,255,0.12)'}`,
                transition: 'all 0.2s',
                fontSize: 12, fontWeight: 700, color: done || active ? 'white' : 'rgba(255,255,255,0.3)',
              }}>
                {done ? <CheckCircle2 size={14} /> : s}
              </div>
              <span style={{
                fontSize: 10.5, fontWeight: active ? 600 : 400,
                color: active ? 'rgba(255,255,255,0.85)' : done ? '#22c55e' : 'rgba(255,255,255,0.3)',
                whiteSpace: 'nowrap',
              }}>
                {STEP_LABELS[s]}
              </span>
            </div>
            {s < 4 && (
              <div style={{
                flex: 1, height: 2, marginBottom: 18, marginLeft: 4, marginRight: 4,
                background: done ? '#22c55e' : 'rgba(255,255,255,0.08)',
                transition: 'background 0.2s',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function SampleDataPanel() {
  const [downloading, setDownloading] = useState<string | null>(null);

  async function handleDownload(count: number, format: 'csv' | 'xlsx') {
    const key = `${count}-${format}`;
    setDownloading(key);
    try {
      const rows = generateRows(count);
      if (format === 'csv') {
        downloadBlob(rowsToCsv(rows), `authmatech-sample-${count}.csv`, 'text/csv');
      } else {
        await downloadXlsx(rows, `authmatech-sample-${count}.xlsx`);
      }
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div style={{
      border: '1px solid rgba(96,165,250,0.15)',
      borderRadius: 12, padding: '16px 18px',
      background: 'rgba(59,130,246,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Sparkles size={14} style={{ color: '#60a5fa' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>
          Generate Sample Data
        </span>
        <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.35)' }}>
          · Realistic telecom CRM data to test the import flow
        </span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {([100, 1000, 10000] as const).map((count) => (
          <div key={count} style={{ display: 'flex', gap: 4 }}>
            {(['csv', 'xlsx'] as const).map((fmt) => {
              const key = `${count}-${fmt}`;
              const isLoading = downloading === key;
              return (
                <button
                  key={fmt}
                  onClick={() => void handleDownload(count, fmt)}
                  disabled={!!downloading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(96,165,250,0.18)',
                    borderRadius: 8, padding: '5px 10px',
                    fontSize: 11.5, color: 'rgba(255,255,255,0.65)',
                    cursor: downloading ? 'not-allowed' : 'pointer',
                    opacity: downloading && !isLoading ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  {isLoading ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                  {count.toLocaleString()} rows · .{fmt}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.28)', lineHeight: 1.5 }}>
        Includes: mobile number, name, email, country, operator, CRM ID, verification status, trust score, fraud risk.
      </div>
    </div>
  );
}

function ReviewStep({
  parseResult,
  mappings,
}: {
  parseResult: ParseResult;
  mappings: Record<string, string>;
}) {
  const mappedCount = Object.keys(mappings).length;
  const reverseMappings: Record<string, string> = {};
  Object.entries(mappings).forEach(([src, tgt]) => { reverseMappings[tgt] = src; });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* File summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
        gap: 12,
      }}>
        {[
          { label: 'File', value: parseResult.fileName },
          { label: 'Format', value: parseResult.fileType },
          { label: 'Total Rows', value: parseResult.totalRows.toLocaleString() },
        ].map((item) => (
          <div key={item.label} style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10, padding: '12px 14px',
          }}>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.35)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {item.label}
            </div>
            <div style={{ fontSize: 13.5, color: 'white', fontWeight: 600, wordBreak: 'break-all' }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>

      {/* Mapping summary */}
      <div style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(96,165,250,0.12)',
        borderRadius: 10, padding: '14px 16px',
      }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Field Mappings ({mappedCount} columns mapped)
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {Object.entries(mappings).map(([src, tgt]) => (
            <span key={src} style={{
              background: 'rgba(59,130,246,0.10)',
              border: '1px solid rgba(96,165,250,0.22)',
              borderRadius: 7, padding: '3px 10px',
              fontSize: 11.5, color: 'rgba(255,255,255,0.70)',
              fontFamily: 'monospace',
            }}>
              {src} → {tgt}
            </span>
          ))}
        </div>
      </div>

      {/* Privacy notice */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)',
        borderRadius: 10, padding: '12px 14px',
        fontSize: 12.5, color: 'rgba(255,255,255,0.50)',
      }}>
        <AlertTriangle size={14} style={{ color: '#60a5fa', flexShrink: 0, marginTop: 1 }} />
        <span>
          Mobile numbers will be stored fully masked (<span style={{ fontFamily: 'monospace', color: 'white', fontWeight: 700 }}>************</span>).
          Existing records matched by mobile number will be updated; new records will be created.
          Raw file bytes are discarded immediately after processing.
        </span>
      </div>
    </div>
  );
}

function SuccessStep({ job }: { job: ImportJob }) {
  const isError = job.status === 'FAILED';
  const isProcessing = job.status === 'PROCESSING' || job.status === 'PENDING';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, padding: '16px 0' }}>
      {isProcessing ? (
        <>
          <Loader2 size={44} className="animate-spin" style={{ color: '#60a5fa' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'white', marginBottom: 6 }}>Import in progress…</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
              Processing {job.totalRows.toLocaleString()} rows. This may take a moment.
            </div>
          </div>
        </>
      ) : isError ? (
        <>
          <XCircle size={44} style={{ color: '#f43f5e' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#f43f5e', marginBottom: 6 }}>Import Failed</div>
            {job.errorSummary && job.errorSummary.length > 0 && (
              <div style={{
                background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.2)',
                borderRadius: 10, padding: '10px 14px', marginTop: 8,
                fontSize: 12, color: '#f43f5e', textAlign: 'left', maxWidth: 480,
              }}>
                {job.errorSummary.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'rgba(52,211,153,0.12)', border: '2px solid rgba(52,211,153,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckCircle2 size={30} style={{ color: '#34d399' }} />
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'white', marginBottom: 4 }}>Import Complete</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>{job.fileName}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, width: '100%', maxWidth: 440 }}>
            {[
              { label: 'Imported', value: job.importedRows, color: '#34d399' },
              { label: 'Updated',  value: job.updatedRows,  color: '#60a5fa' },
              { label: 'Skipped',  value: job.skippedRows,  color: '#fbbf24' },
              { label: 'Errors',   value: job.errorRows,    color: '#f43f5e' },
            ].map((s) => (
              <div key={s.label} style={{
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10, padding: '12px 8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value.toLocaleString()}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {job.errorSummary && job.errorSummary.length > 0 && (
            <div style={{
              background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.22)',
              borderRadius: 10, padding: '10px 14px', width: '100%', maxWidth: 480,
            }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: '#fbbf24', marginBottom: 6 }}>
                Row errors ({job.errorSummary.length})
              </div>
              <div style={{ fontSize: 11.5, color: '#fbbf24', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {job.errorSummary.slice(0, 5).map((e, idx) => <div key={idx}>{e}</div>)}
                {job.errorSummary.length > 5 && (
                  <div style={{ opacity: 0.6 }}>+{job.errorSummary.length - 5} more…</div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ImportWizard({ canManage }: { canManage: boolean }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [startedJobId, setStartedJobId] = useState<string | null>(null);
  // Immediately stores the job returned synchronously from /import/start to avoid a loading flash
  const [immediateJob, setImmediateJob] = useState<ImportJob | null>(null);

  // Poll only if the job is still in PROCESSING/PENDING after the synchronous response
  const jobQuery = useQuery<ImportJob>({
    queryKey: ['import-job-detail', startedJobId],
    queryFn: async () => {
      const jobs = await unwrap(
        await api.get<ResponseEnvelope<ImportJob[]>>(Endpoints.me.connect.importJobs)
      );
      const job = jobs.find((j: ImportJob) => j.id === startedJobId);
      if (!job) throw new Error('Job not found');
      return job;
    },
    enabled: !!startedJobId && step === 4 &&
      (immediateJob?.status === 'PROCESSING' || immediateJob?.status === 'PENDING'),
    refetchInterval: (q) => {
      const d = q.state.data as ImportJob | undefined;
      return d && (d.status === 'PROCESSING' || d.status === 'PENDING') ? 2000 : false;
    },
  });

  const parseMutation = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return unwrap(
        await api.post<ResponseEnvelope<ParseResult>>(Endpoints.me.connect.importParse, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
    onSuccess: (result: ParseResult) => {
      setParseResult(result);
      setMappings(result.suggestedMappings ?? {});
      setParseError(null);
      setStep(2);
    },
    onError: (e) => setParseError((e as Error).message),
  });

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!parseResult) throw new Error('No parse result');
      return unwrap(
        await api.post<ResponseEnvelope<ImportJob>>(Endpoints.me.connect.importStart, {
          jobId: parseResult.jobId,
          fieldMappings: mappings,
        })
      );
    },
    onSuccess: (job: ImportJob) => {
      setImmediateJob(job);
      setStartedJobId(job.id);
      setStep(4);
      void queryClient.invalidateQueries({ queryKey: ['connect-import-jobs'] });
    },
    onError: (e) => setParseError((e as Error).message),
  });

  const reset = useCallback(() => {
    setStep(1);
    setParseResult(null);
    setMappings({});
    setParseError(null);
    setStartedJobId(null);
    setImmediateJob(null);
    void queryClient.invalidateQueries({ queryKey: ['connect-import-jobs'] });
  }, [queryClient]);

  const hasMobileMapping = Object.values(mappings).includes('mobileNumber');

  if (!canManage) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '48px 0', gap: 12, color: 'rgba(255,255,255,0.35)',
      }}>
        <FileSpreadsheet size={36} style={{ opacity: 0.3 }} />
        <p style={{ margin: 0, fontSize: 14 }}>
          Import is available to Client Admins and Operators only.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #050e1d 0%, #060f22 100%)',
      border: '1px solid rgba(96,165,250,0.10)',
      borderRadius: 16, padding: '28px 28px 24px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <BarChart3 size={18} style={{ color: '#60a5fa' }} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'white' }}>
            Import CRM Data
          </h2>
          <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.40)' }}>
            Upload a CSV or XLSX file to sync customer records into Authmatech Connect
          </p>
        </div>
      </div>

      <StepIndicator current={step} />

      {/* Step 1: Upload */}
      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <FileUploadZone
            onFile={(file) => parseMutation.mutate(file)}
            isLoading={parseMutation.isPending}
            error={parseError}
          />
          <SampleDataPanel />
        </div>
      )}

      {/* Step 2: Map Fields */}
      {step === 2 && parseResult && (
        <FieldMappingStep
          headers={parseResult.headers}
          sampleRows={parseResult.sampleRows}
          mappings={mappings}
          onChange={setMappings}
        />
      )}

      {/* Step 3: Review */}
      {step === 3 && parseResult && (
        <ReviewStep parseResult={parseResult} mappings={mappings} />
      )}

      {/* Step 4: Result — prefer live poll data, fall back to synchronous mutation result */}
      {step === 4 && (
        <SuccessStep job={jobQuery.data ?? immediateJob ?? {
          id: startedJobId ?? '',
          jobName: '',
          fileName: parseResult?.fileName ?? '',
          status: 'PROCESSING',
          totalRows: parseResult?.totalRows ?? 0,
          importedRows: 0,
          updatedRows: 0,
          skippedRows: 0,
          errorRows: 0,
          errorSummary: null,
          completedAt: null,
        }} />
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        {step > 1 && step < 4 ? (
          <button
            onClick={() => setStep((s) => (s - 1) as Step)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 9, padding: '8px 16px',
              fontSize: 13.5, color: 'rgba(255,255,255,0.55)',
              cursor: 'pointer',
            }}
          >
            <ArrowLeft size={14} /> Back
          </button>
        ) : (
          <div />
        )}

        {step === 1 && (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.30)' }}>
            Select a file to continue
          </div>
        )}

        {step === 2 && (
          <button
            onClick={() => setStep(3)}
            disabled={!hasMobileMapping}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: hasMobileMapping ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'rgba(255,255,255,0.06)',
              border: 'none', borderRadius: 9, padding: '8px 18px',
              fontSize: 13.5, fontWeight: 600,
              color: hasMobileMapping ? 'white' : 'rgba(255,255,255,0.30)',
              cursor: hasMobileMapping ? 'pointer' : 'not-allowed',
              boxShadow: hasMobileMapping ? '0 4px 14px rgba(59,130,246,0.35)' : 'none',
            }}
          >
            Review Import <ArrowRight size={14} />
          </button>
        )}

        {step === 3 && (
          <button
            onClick={() => startMutation.mutate()}
            disabled={startMutation.isPending || !hasMobileMapping}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'linear-gradient(135deg, #34d399, #10b981)',
              border: 'none', borderRadius: 9, padding: '8px 20px',
              fontSize: 13.5, fontWeight: 600, color: '#0a0a0a',
              cursor: startMutation.isPending ? 'wait' : 'pointer',
              boxShadow: '0 4px 14px rgba(52,211,153,0.35)',
              opacity: startMutation.isPending ? 0.7 : 1,
            }}
          >
            {startMutation.isPending
              ? <><Loader2 size={14} className="animate-spin" /> Starting…</>
              : <><Upload size={14} /> Start Import</>
            }
          </button>
        )}

        {step === 4 && (
          <button
            onClick={reset}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 9, padding: '8px 16px',
              fontSize: 13.5, color: 'rgba(255,255,255,0.65)',
              cursor: 'pointer',
            }}
          >
            <RefreshCcw size={13} /> Import Another File
          </button>
        )}
      </div>
    </div>
  );
}
